import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { FloatingActionButton } from '../../models/common';

@Injectable({ providedIn: 'root' })
export class FloatingActionButtonService {
    private _actionButtons: FloatingActionButton[] = [];
    private _actionButtonsSubject = new BehaviorSubject<FloatingActionButton[]>(
        this._actionButtons
    );

    public buttonsStream: Observable<
        FloatingActionButton[]
    > = this._actionButtonsSubject.asObservable();
    public isAllowedStream: Observable<boolean>;

    constructor(private _router: Router, @Inject(SETTINGS) private _settings: ISettings) {
        SingletonServiceGuard.register('FloatingActionButtonService');
        this._router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => {
            this.filterOutLocalFabs();
        });

        this.isAllowedStream = this._router.events.pipe(
            filter<NavigationEnd>((e) => e instanceof NavigationEnd),
            map(({ urlAfterRedirects: url }) => url),
            startWith(location.href),
            map((url) =>
                this._settings.fabBlacklistedRoutePatterns.every((blacklistPattern) => {
                    const regex =
                        typeof blacklistPattern === 'string'
                            ? new RegExp(`^${blacklistPattern}$`)
                            : new RegExp(blacklistPattern);
                    return !url.match(regex);
                })
            )
        );
    }

    public addAction(button: FloatingActionButton): void {
        // Remove FAB if we are readding it so that the newest one is being used.
        if (button.identifier) {
            this.removeFab(button.identifier);
        }
        this._actionButtons.push(button);
        this._actionButtonsSubject.next(this._actionButtons);
    }

    private filterOutLocalFabs(): void {
        this._actionButtons = this._actionButtons.filter(
            (action: FloatingActionButton) => action.global
        );
        this._actionButtonsSubject.next(this._actionButtons);
    }

    public removeFab(identifier: string): void {
        this._actionButtons = this._actionButtons.filter((action: FloatingActionButton) => {
            if (action.identifier) {
                return action.identifier !== identifier;
            } else {
                return true;
            }
        });
        this._actionButtonsSubject.next(this._actionButtons);
    }
}
